import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from '@environment/environment';
import { ROUTE_CONSTANTS } from '@app/core/constants/route.constant';
import { AnalyticsService } from '@app/core/services/infrastructure/analytics/analytics.service';
import { PAYWALL_CONSTANTS } from '@app/modules/paywall/constants/paywall.constants';
import { IOnboardingStepDef, laterTourStep, OnboardingTourStep } from '../interfaces/onboarding.interface';

/**
 * The guided first session, step by step: the checklist (account done), the switch between a single
 * drill and a full session, the session's basic data, players and keepers, the comment field. Each
 * step but the first points at `[data-tour="<step>"]`.
 */
export type FormTourStep = 'checklist' | 'switch' | 'options' | 'players' | 'comment';
export const SIDEKICK_TOUR: FormTourStep[] = ['checklist', 'switch', 'options', 'players', 'comment'];

/**
 * The tour through the coach's first session, on the session page: how it is built — its drills in
 * an order to drag & drop (a full session only), the editable text, the match label, where to prompt
 * again and the PDF export. Each step but the first points at `[data-tour="<step>"]`.
 */
export type SessionTourStep = 'sessionStart' | 'drills' | 'edit' | 'quality' | 'reprompt' | 'pdf';
export const SIDEKICK_SESSION_TOUR: SessionTourStep[] = ['sessionStart', 'drills', 'edit', 'quality', 'reprompt', 'pdf'];

export type SidekickTourStep = FormTourStep | SessionTourStep;
/** Tour steps that talk without pointing at anything on the page. */
export const SIDEKICK_UNTARGETED_STEPS: SidekickTourStep[] = ['checklist', 'sessionStart'];

/**
 * Moments the sidekick speaks up. Each message is shown at most once per coach; the upgrade and
 * the reminder of the next getting-started step once per visit.
 */
export type SidekickEvent =
    | 'birth'
    | 'firstVisit'
    | `tour.${SidekickTourStep}`
    | 'sessionTour'
    | 'tourComplete'
    | 'commentHint'
    | 'session1'
    | 'session2'
    | 'session3'
    | 'upgrade'
    | 'nextStep'
    | `stepDone.${IOnboardingStepDef['key']}`
    | 'onboardingDone'
    | 'nameMe'
    | 'named'
    | 'calendarNextUp';

export interface ISidekickMessage {
    event: SidekickEvent;
    /** Translation key of the bubble text. */
    text: string;
    /** Values for the text and the CTA label, as translation keys (e.g. `{ step: 'onboarding.steps.player.title' }`). */
    params?: Record<string, string>;
    /** The sidekick jumps (celebration) before it talks. */
    cheer: boolean;
    /** `params` and the CTA label are ready-made text, not translation keys (page messages). */
    plain?: boolean;
    /** Stays open until the coach acts on it (the guided first session). */
    sticky?: boolean;
    /** At most one call to action: a link (`route`) or something the sidekick does itself (`action`). */
    cta?: {
        label: string;
        route?: string[];
        queryParams?: Record<string, string>;
        /** `external`: runs what the page handed over with the message (e.g. the calendar's next step). */
        action?: 'name' | 'tourNext' | 'celebrationNext' | 'external';
    };
}

/** Longest name for the co-coach; the backend checks the same. */
export const SIDEKICK_NAME_MAX_LENGTH = 20;
/** Letters of any language, digits, spaces and simple punctuation. */
export const SIDEKICK_NAME_PATTERN = /^[\p{L}\p{N} .'-]+$/u;

/** The name as it is stored: trimmed, single spaces; null when it can't be used. */
export function normalizeSidekickName(value: string | null | undefined): string | null {
    const name = (value ?? '').trim().replace(/\s+/g, ' ');
    return name.length > 0 && name.length <= SIDEKICK_NAME_MAX_LENGTH && SIDEKICK_NAME_PATTERN.test(name) ? name : null;
}

type FixedSidekickEvent = 'firstVisit' | 'commentHint' | 'session1' | 'session2' | 'session3' | 'upgrade' | 'nameMe';

const MESSAGES: Record<FixedSidekickEvent, ISidekickMessage> = {
    // Coaches who were here before the sidekick became their co-coach, or who skipped naming it
    nameMe: {
        event: 'nameMe',
        text: 'sidekick.naming.prompt',
        cheer: false,
        cta: { label: 'sidekick.naming.promptCta', action: 'name' },
    },
    firstVisit: { event: 'firstVisit', text: 'sidekick.messages.firstVisit', cheer: false },
    commentHint: { event: 'commentHint', text: 'sidekick.messages.commentHint', cheer: false },
    session1: { event: 'session1', text: 'sidekick.messages.session1', cheer: true },
    session2: { event: 'session2', text: 'sidekick.messages.session2', cheer: true },
    session3: { event: 'session3', text: 'sidekick.messages.session3', cheer: true },
    upgrade: {
        event: 'upgrade',
        text: 'sidekick.messages.upgrade',
        cheer: false,
        cta: {
            label: 'sidekick.messages.upgradeCta',
            route: ['/', ROUTE_CONSTANTS.UPGRADE],
            queryParams: { source: PAYWALL_CONSTANTS.SOURCES.SIDEKICK },
        },
    },
};

/** Messages that may come back in the next visit; all others are shown once per coach. */
const ONCE_PER_VISIT: SidekickEvent[] = ['upgrade', 'nextStep'];
/** Answers to something the coach just did: shown every time. */
const ALWAYS: SidekickEvent[] = ['named', 'calendarNextUp'];
/** The steps of the guided session follow the saved onboarding step, not the once-only rule. */
const isTourEvent = (event: SidekickEvent): boolean => event.startsWith('tour.');

export const SIDEKICK_STORAGE = {
    /** Per coach: events already shown (`…seen.<user id>`). */
    SEEN_PREFIX: 'saideline.sidekick.seen.',
    /** On this device: the coach muted the tips. */
    MUTED: 'saideline.sidekick.muted',
    /** This visit (browser tab): the coach hid the sidekick. */
    HIDDEN: 'saideline.sidekick.hidden',
    /** This visit: events already shown that may come back next visit. */
    SEEN_THIS_VISIT: 'saideline.sidekick.seenThisVisit',
    /** This visit: the intro already played. */
    INTRO_PLAYED: 'saideline.sidekick.introPlayed',
} as const;

/**
 * The Saideline sidekick: the logo mark as a small co-coach in the bottom-right corner.
 * Rule-based, no AI: it greets new coaches, walks them through the getting-started steps,
 * celebrates their first three sessions and, only for coaches without a subscription, offers the
 * plans once the paywall may no longer open by itself.
 * Only active where `environment.SIDEKICK_ENABLED` is set (testing, development).
 */
@Injectable({ providedIn: 'root' })
export class SidekickService {
    /** What the CTA of a page's message (`action: 'external'`) does. */
    private externalAction: (() => void) | null = null;
    /** Set from the environment; tests switch it on. */
    enabled = !!environment.SIDEKICK_ENABLED;

    private readonly messageSubject$ = new BehaviorSubject<ISidekickMessage | null>(null);
    private readonly busySubject$ = new BehaviorSubject<boolean>(false);
    private readonly mutedSubject$ = new BehaviorSubject<boolean>(
        this.read(localStorage, SIDEKICK_STORAGE.MUTED) === 'true'
    );
    private readonly hiddenSubject$ = new BehaviorSubject<boolean>(
        this.read(sessionStorage, SIDEKICK_STORAGE.HIDDEN) === 'true'
    );

    private readonly tourStepSubject$ = new BehaviorSubject<SidekickTourStep | null>(null);

    /** The step of the guided first session in progress, if any. */
    readonly tourStep$: Observable<SidekickTourStep | null> = this.tourStepSubject$.asObservable();
    /** The open speech bubble, if any. */
    readonly message$: Observable<ISidekickMessage | null> = this.messageSubject$.asObservable();
    /** A session is being generated: the sidekick looks attentive. */
    readonly busy$: Observable<boolean> = this.busySubject$.asObservable();
    readonly muted$: Observable<boolean> = this.mutedSubject$.asObservable();
    readonly hidden$: Observable<boolean> = this.hiddenSubject$.asObservable();

    private readonly progressSubject$ = new Subject<OnboardingTourStep>();
    /** The onboarding moved on: saved on the coach's account, so it goes on from there next time. */
    readonly progress$: Observable<OnboardingTourStep> = this.progressSubject$.asObservable();

    private readonly sessionTourDoneSubject$ = new Subject<void>();
    /** The tour through the first session is over: the checklist shows the way on from here. */
    readonly sessionTourDone$: Observable<void> = this.sessionTourDoneSubject$.asObservable();

    private readonly seenSubject$ = new Subject<SidekickEvent>();
    /** A message shown once per coach was seen: saved on the account, so no other device shows it again. */
    readonly seen$: Observable<SidekickEvent> = this.seenSubject$.asObservable();

    private userId: string | null = null;
    /** The coach's onboarding step still to show (from the account); `birth` until it started. */
    private progress: OnboardingTourStep = 'birth';
    /** This visit: the birth already started, or the guided session was already shown. */
    private birthTakenThisVisit = false;
    private tourShownThisVisit = false;
    /** The steps of the tour in progress; the form tour unless the session tour runs. */
    private tourSteps: SidekickTourStep[] = SIDEKICK_TOUR;
    /** A celebration held back while the session tour runs; it follows the tour. */
    private pendingCelebration: ISidekickMessage | null = null;
    /** Used when storage is blocked, so the once-only rules still hold until the next reload. */
    private readonly fallbackSeen = new Set<string>();
    /** Messages shown once per coach that the coach has seen on any device (from the account). */
    private readonly accountSeen = new Set<string>();

    constructor(private readonly analytics: AnalyticsService) {}

    get message(): ISidekickMessage | null {
        return this.messageSubject$.value;
    }

    get isMuted(): boolean {
        return this.mutedSubject$.value;
    }

    /**
     * The signed-in coach whose once-only messages are tracked; null when signed out.
     * @param progress - The coach's saved onboarding step; absent until the onboarding started.
     */
    setUser(userId: string | null, progress: OnboardingTourStep = 'birth'): void {
        if (userId !== this.userId) {
            this.userId = userId;
            this.birthTakenThisVisit = false;
            this.tourShownThisVisit = false;
            this.pendingCelebration = null;
            this.accountSeen.clear();
            this.clearMessage();
            this.progress = progress;
            return;
        }
        // A cached session may be older than what this device already did: the onboarding only moves on
        this.progress = laterTourStep(this.progress, progress);
    }

    /**
     * Takes over what the coach's account knows, so switching devices never starts the onboarding over:
     * the step reached on any device and the messages already seen there.
     * @returns The onboarding step still to show
     */
    syncAccount(progress: OnboardingTourStep | undefined, seen: string[] = []): OnboardingTourStep {
        this.progress = laterTourStep(this.progress, progress);
        seen.forEach((event) => this.accountSeen.add(event));
        return this.progress;
    }

    /** The coach's onboarding step still to show. */
    get onboardingStep(): OnboardingTourStep {
        return this.progress;
    }

    /**
     * A test account starts over as a new coach at sign-in: forgets what it has seen, muted or hidden
     * on this device. The backend resets its onboarding step and session count.
     */
    forgetCoach(userId: string): void {
        this.removeItem(localStorage, `${SIDEKICK_STORAGE.SEEN_PREFIX}${userId}`);
        this.removeItem(sessionStorage, `${SIDEKICK_STORAGE.SEEN_THIS_VISIT}.${userId}`);
        this.removeItem(localStorage, SIDEKICK_STORAGE.MUTED);
        this.removeItem(sessionStorage, SIDEKICK_STORAGE.HIDDEN);
        this.removeItem(sessionStorage, SIDEKICK_STORAGE.INTRO_PLAYED);
        this.fallbackSeen.clear();
        this.accountSeen.clear();
        this.progress = 'birth';
        this.mutedSubject$.next(false);
        this.hiddenSubject$.next(false);
        this.birthTakenThisVisit = false;
        this.tourShownThisVisit = false;
    }

    /** The onboarding (birth and guided session) is finished for good. */
    get isOnboardingDone(): boolean {
        return this.progress === 'done';
    }

    /** The birth played (or was skipped): the guided session is next. */
    finishBirth(): void {
        if (this.progress === 'birth') {
            this.advance('checklist');
        }
    }

    /**
     * Asks a coach whose co-coach has no name yet to give it one, once. Counts as this visit's
     * reminder of the next getting-started step.
     */
    askForName(): boolean {
        const shown = this.show('nameMe');
        if (shown) {
            this.markSeen('nextStep');
        }
        return shown;
    }

    /** The co-coach has no name yet and may ask for one: the coach has not said "later" to it. */
    get mayAskForName(): boolean {
        return this.canSpeak() && !this.hasSeen('nameMe');
    }

    /** The coach skipped naming the co-coach: don't ask again (the menu still offers it). */
    skipNaming(): void {
        if (this.userId) {
            this.markSeen('nameMe');
        }
        this.analytics.trackSidekick('name_skip');
    }

    /** The coach named (or renamed) the co-coach: it says hello with its new name. */
    confirmName(name: string, isRename: boolean): void {
        if (this.userId) {
            this.markSeen('nameMe');
        }
        this.analytics.trackSidekick(isRename ? 'rename' : 'name');
        if (isRename) {
            this.show({ event: 'named', text: 'sidekick.naming.renamed', params: { name }, cheer: true });
        }
    }

    /** The greeting also counts as this visit's reminder of the next getting-started step. */
    greetFirstVisit(): boolean {
        const shown = this.show('firstVisit');
        if (shown) {
            this.markSeen('nextStep');
        }
        return shown;
    }

    get tourStep(): SidekickTourStep | null {
        return this.tourStepSubject$.value;
    }

    /**
     * Starts the guided session after the birth, or goes on where the coach left it last time; at
     * most once per visit, and never again once it was finished.
     * @param withChecklist - false when the checklist can't be shown; that step is skipped
     * @returns false when the sidekick can't speak (muted, hidden, disabled), the birth is still to
     * come, the tour was finished or it was already shown this visit
     */
    startTour(withChecklist = true): boolean {
        const saved = this.progress;
        if (!this.canSpeak() || saved === 'birth' || saved === 'done' || this.tourShownThisVisit) {
            return false;
        }
        this.tourShownThisVisit = true;
        this.tourSteps = SIDEKICK_TOUR;
        const step: FormTourStep = saved === 'checklist' && !withChecklist ? SIDEKICK_TOUR[1] : saved;
        this.advance(step);
        return this.showTourStep(step);
    }

    /**
     * Right after the coach's first session, on its page: shows how it is built, once per coach.
     * The celebration of the first session follows the tour.
     * @param isFullSession - false for a single drill, which has no drill order to change
     */
    startSessionTour(isFullSession: boolean): boolean {
        if (!this.canSpeak() || this.tourStepSubject$.value || this.hasSeen('sessionTour')) {
            return false;
        }
        this.markSeen('sessionTour');
        const current = this.messageSubject$.value;
        if (current?.event === 'session1') {
            this.pendingCelebration = current;
        }
        this.tourSteps = SIDEKICK_SESSION_TOUR.filter((step) => isFullSession || step !== 'drills');
        this.analytics.trackSidekick('shown', 'sessionTour');
        return this.showTourStep(this.tourSteps[0]);
    }

    /** The coach is ready for the next step; after the last one the onboarding is done for good. */
    nextTourStep(): void {
        const step = this.tourStepSubject$.value;
        if (!step) {
            return;
        }
        this.analytics.trackSidekick('cta_click', `tour.${step}`);
        const next = this.tourSteps[this.tourSteps.indexOf(step) + 1];
        if (this.isFormTourStep(step)) {
            this.advance(next && this.isFormTourStep(next) ? next : 'done');
        }
        if (!next || !this.showTourStep(next)) {
            this.clearMessage();
            if (!next && !this.isFormTourStep(step)) {
                this.celebrateOnboarding();
            } else {
                this.showPendingCelebration();
            }
        }
    }

    /** After the click on a celebration's "Next": what it held back follows. */
    continueCelebration(): void {
        this.clearMessage();
        this.showPendingCelebration();
    }

    /**
     * Ends the guided session for this visit, e.g. when the coach closes it or leaves the training
     * form. Not finished yet, it goes on at the same step next time.
     */
    endTour(): void {
        if (this.tourStepSubject$.value) {
            this.clearMessage();
            this.pendingCelebration = null;
        }
    }

    /**
     * Before the first session, when the coach left the comment field empty: points to it, once.
     * @returns false when the sidekick can't say it (muted, hidden, disabled or already said)
     */
    pointToComment(): boolean {
        return this.show('commentHint');
    }

    /**
     * @param sessionCount - Sessions the coach has created so far; only the first three are celebrated.
     * @param next - The next open getting-started step, offered as the way on.
     */
    celebrateSession(sessionCount: number, next?: IOnboardingStepDef | null): boolean {
        const event = (['session1', 'session2', 'session3'] as FixedSidekickEvent[])[sessionCount - 1];
        if (!event) {
            return false;
        }
        const message =
            sessionCount === 1 && next?.key === 'playerAdded'
                ? // The first session leads on to the squad: with players Saideline splits the teams by position
                  {
                      ...MESSAGES.session1,
                      text: 'sidekick.messages.session1Squad',
                      cta: { label: 'sidekick.messages.session1SquadCta', route: next.route },
                  }
                : this.withNextStep(MESSAGES[event], next);
        if (event === 'session1' && this.isSessionTourRunning()) {
            // Said once the tour through the first session is over
            this.pendingCelebration = this.hasSeen(event) ? null : message;
            this.markSeen(event);
            return false;
        }
        return this.show(message);
    }

    /**
     * Reminds the coach of the next getting-started step, once per visit.
     * @param withCta - false when the coach is already on the page of that step.
     */
    remindNextStep(step: IOnboardingStepDef, withCta: boolean): boolean {
        const message: ISidekickMessage = {
            event: 'nextStep',
            text: 'sidekick.onboarding.next',
            params: { step: `${step.label}.title`, hint: `${step.label}.hint` },
            cheer: false,
        };
        return this.show(withCta ? { ...message, cta: { label: 'sidekick.onboarding.go', route: step.route } } : message);
    }

    /** Celebrates a getting-started step the coach just finished and points to the next one. */
    celebrateStep(done: IOnboardingStepDef, next: IOnboardingStepDef | null): boolean {
        if (!next) {
            return this.show({ event: 'onboardingDone', text: 'sidekick.onboarding.allDone', cheer: true });
        }
        return this.show(
            this.withNextStep(
                {
                    event: `stepDone.${done.key}`,
                    text: 'sidekick.onboarding.stepDone',
                    params: { done: `${done.label}.title` },
                    cheer: true,
                },
                next
            )
        );
    }

    /**
     * Offers the plans to a coach without free sessions. Callers use it only where the paywall
     * would otherwise not open, so the two never compete.
     * @returns false when the sidekick can't say it (muted, hidden, disabled or already said this visit)
     */
    offerUpgrade(): boolean {
        return this.show('upgrade');
    }

    /** Whether the co-coach can talk right now (not muted, hidden or switched off). */
    get canTalk(): boolean {
        return this.canSpeak();
    }

    /**
     * The calendar's "Als Nächstes", said by the co-coach instead of a bar on the page: the next
     * thing to do and its one action. The calendar decides how often (once per visit and entry).
     */
    sayNextUp(text: string, cta: string, act: () => void): boolean {
        this.externalAction = act;
        return this.show({
            event: 'calendarNextUp',
            text: 'sidekick.calendar.nextUp',
            params: { text },
            plain: true,
            cheer: false,
            cta: { label: cta, action: 'external' },
        });
    }

    /** Runs (once) what the page handed over with its message. */
    runExternalAction(): void {
        const act = this.externalAction;
        this.externalAction = null;
        act?.();
    }

    setBusy(isBusy: boolean): void {
        if (this.busySubject$.value !== isBusy) {
            this.busySubject$.next(isBusy);
        }
    }

    /** Closes the bubble. @param reason - `timeout` when it closed by itself (not tracked). */
    closeMessage(reason: 'close' | 'timeout' | 'cta' = 'close'): void {
        const message = this.messageSubject$.value;
        if (!message) {
            return;
        }
        if (this.tourStepSubject$.value || message.event === 'tourComplete') {
            // Closing a tour or its celebration skips what was to follow it
            this.pendingCelebration = null;
        }
        this.clearMessage();
        if (reason === 'close') {
            this.analytics.trackSidekick('close', message.event);
        } else if (reason === 'cta') {
            this.analytics.trackSidekick('cta_click', message.event);
        }
    }

    /** Mutes or unmutes the tips on this device. Muted, the sidekick stays but never talks. */
    toggleMuted(): void {
        const muted = !this.mutedSubject$.value;
        this.mutedSubject$.next(muted);
        this.write(localStorage, SIDEKICK_STORAGE.MUTED, String(muted));
        if (muted) {
            this.clearMessage();
        }
        this.analytics.trackSidekick(muted ? 'mute' : 'unmute');
    }

    /** Hides the sidekick until the next visit. */
    hideForNow(): void {
        this.hiddenSubject$.next(true);
        this.clearMessage();
        this.write(sessionStorage, SIDEKICK_STORAGE.HIDDEN, 'true');
        this.analytics.trackSidekick('hide');
    }

    /**
     * Whether the sidekick may still put itself together in front of the coach (once per coach, the
     * first time on the start page, whether new or not); marks it as played. It replaces this visit's intro.
     */
    takeBirth(): boolean {
        if (
            !this.enabled ||
            !this.userId ||
            this.hiddenSubject$.value ||
            this.progress !== 'birth' ||
            this.birthTakenThisVisit
        ) {
            return false;
        }
        this.birthTakenThisVisit = true;
        this.write(sessionStorage, SIDEKICK_STORAGE.INTRO_PLAYED, 'true');
        this.analytics.trackSidekick('shown', 'birth');
        return true;
    }

    /** Whether the intro may still play in this visit; marks it as played. */
    takeIntro(): boolean {
        if (this.read(sessionStorage, SIDEKICK_STORAGE.INTRO_PLAYED) === 'true') {
            return false;
        }
        this.write(sessionStorage, SIDEKICK_STORAGE.INTRO_PLAYED, 'true');
        return true;
    }

    private show(eventOrMessage: FixedSidekickEvent | ISidekickMessage): boolean {
        const message = typeof eventOrMessage === 'string' ? MESSAGES[eventOrMessage] : eventOrMessage;
        const event = message.event;
        if (!this.canSpeak()) {
            return false;
        }
        if (!ALWAYS.includes(event) && !isTourEvent(event)) {
            if (this.hasSeen(event)) {
                return false;
            }
            this.markSeen(event);
        }
        this.messageSubject$.next(message);
        this.analytics.trackSidekick('shown', event);
        return true;
    }

    private canSpeak(): boolean {
        return this.enabled && !!this.userId && !this.mutedSubject$.value && !this.hiddenSubject$.value;
    }

    private isFormTourStep(step: SidekickTourStep): step is FormTourStep {
        return (SIDEKICK_TOUR as SidekickTourStep[]).includes(step);
    }

    private isSessionTourRunning(): boolean {
        const step = this.tourStepSubject$.value;
        return !!step && !this.isFormTourStep(step);
    }

    /**
     * The whole onboarding is done (the tour through the first session was its last part): the
     * co-coach throws its dots as confetti and says so; on "Next" the checklist shows the way on.
     */
    private celebrateOnboarding(): void {
        // The checklist takes over and names the next step: the held-back celebration is not needed
        this.pendingCelebration = null;
        this.sessionTourDoneSubject$.next();
        this.show({
            event: 'tourComplete',
            text: 'sidekick.tour.complete',
            cheer: true,
            sticky: true,
            cta: { label: 'sidekick.tour.next', action: 'celebrationNext' },
        });
    }

    private showPendingCelebration(): void {
        const message = this.pendingCelebration;
        this.pendingCelebration = null;
        if (message && this.canSpeak()) {
            this.messageSubject$.next(message);
            this.analytics.trackSidekick('shown', message.event);
        }
    }

    private showTourStep(step: SidekickTourStep): boolean {
        const isLast = step === this.tourSteps[this.tourSteps.length - 1];
        const shown = this.show({
            event: `tour.${step}`,
            text: `sidekick.tour.${step}`,
            cheer: step === 'checklist' || step === 'sessionStart',
            sticky: true,
            cta: { label: isLast ? 'sidekick.tour.finish' : 'sidekick.tour.next', action: 'tourNext' },
        });
        if (shown) {
            this.tourStepSubject$.next(step);
        }
        return shown;
    }

    /** Moves the saved onboarding step on; `done` is final. */
    private advance(step: OnboardingTourStep): void {
        if (this.progress === step || this.progress === 'done') {
            return;
        }
        this.progress = step;
        this.progressSubject$.next(step);
    }

    /** Closes the bubble; a step of the guided first session ends the tour with it. */
    private clearMessage(): void {
        this.messageSubject$.next(null);
        if (this.tourStepSubject$.value) {
            this.tourStepSubject$.next(null);
        }
    }

    /** A celebration without its own button leads on to the next getting-started step. */
    private withNextStep(message: ISidekickMessage, next?: IOnboardingStepDef | null): ISidekickMessage {
        if (!next || message.cta) {
            return message;
        }
        return {
            ...message,
            params: { ...message.params, step: `${next.label}.title` },
            cta: { label: 'sidekick.onboarding.continue', route: next.route },
        };
    }

    private hasSeen(event: SidekickEvent): boolean {
        const key = this.seenKey(event);
        return (
            (!ONCE_PER_VISIT.includes(event) && this.accountSeen.has(event)) ||
            this.fallbackSeen.has(`${key}:${event}`) ||
            this.readList(key).includes(event)
        );
    }

    private markSeen(event: SidekickEvent): void {
        const key = this.seenKey(event);
        this.fallbackSeen.add(`${key}:${event}`);
        const isOncePerVisit = ONCE_PER_VISIT.includes(event);
        const storage = isOncePerVisit ? sessionStorage : localStorage;
        this.write(storage, key, JSON.stringify([...this.readList(key), event]));
        if (!isOncePerVisit && !this.accountSeen.has(event)) {
            this.accountSeen.add(event);
            this.seenSubject$.next(event);
        }
    }

    private seenKey(event: SidekickEvent): string {
        return ONCE_PER_VISIT.includes(event)
            ? `${SIDEKICK_STORAGE.SEEN_THIS_VISIT}.${this.userId}`
            : `${SIDEKICK_STORAGE.SEEN_PREFIX}${this.userId}`;
    }

    private readList(key: string): string[] {
        const storage = key.startsWith(SIDEKICK_STORAGE.SEEN_THIS_VISIT) ? sessionStorage : localStorage;
        try {
            const parsed = JSON.parse(this.read(storage, key) ?? '[]');
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }

    private read(storage: Storage, key: string): string | null {
        try {
            return storage.getItem(key);
        } catch {
            return null;
        }
    }

    private write(storage: Storage, key: string, value: string): void {
        try {
            storage.setItem(key, value);
        } catch {
            // Storage blocked: the in-memory state applies until the next reload
        }
    }

    private removeItem(storage: Storage, key: string): void {
        try {
            storage.removeItem(key);
        } catch {
            // Storage blocked: nothing stored either
        }
    }
}
